import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Target, Eye, TrendingUp, AlertTriangle, ChevronLeft, ChevronRight,
  Lightbulb, FileEdit
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

/**
 * REUSABLE PERFORMANCE TRACKER COMPONENT
 * Used in Channel Overview and Channel Performance pages
 * Shows monthly progress with horizontal scroll, insight modal, and AI suggestions
 */

// Section Header (local copy for standalone use)
function SectionHeader({ icon: Icon, title, subtitle }) {
  return (
    <div className="px-6 py-4 border-b border-[hsl(var(--border))] bg-gradient-to-r from-[#E63946]/20 via-[#F97316]/15 to-transparent relative overflow-hidden rounded-t-xl">
      <div className="absolute inset-0 bg-gradient-to-r from-[#E63946]/10 to-transparent opacity-50" />
      <div className="relative flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-gradient-to-br from-[#E63946]/30 to-[#F97316]/20 border border-[#E63946]/40 shadow-[0_0_15px_-3px_#E63946]">
          <Icon size={20} className="text-white" />
        </div>
        <div>
          <h3 className="text-lg font-bold text-white font-heading">{title}</h3>
          <p className="text-xs text-[hsl(var(--text-muted))]">{subtitle}</p>
        </div>
      </div>
    </div>
  );
}

// AI Strategy Database
const strategyDatabase = {
  poor: { // < 50%
    subscribers: [
      { title: 'Telegram Ads Burst Campaign', desc: 'Lancarkan iklan Telegram targetkan group forex dengan tawaran eksklusif 48 jam. Budget RM500-1000 untuk 5000 subscribers baru.', impact: 'HIGH' },
      { title: 'Collaboration dengan Influencer', desc: 'Partner dengan 2-3 influencer forex (10K+ followers). Mereka promote channel anda dengan testimoni live trading.', impact: 'HIGH' },
      { title: 'Giveaway Contest', desc: 'Anjurkan contest "Join & Share" dengan hadiah modal trading RM500. Syarat: follow channel + tag 3 kawan.', impact: 'MEDIUM' },
    ],
    leads: [
      { title: 'Webinar Percuma + Bonus Signal', desc: 'Anjurkan webinar "Teknik Scalping Gold" dengan bonus 1 minggu signal percuma untuk yang register. Lead magnet proven convert.', impact: 'HIGH' },
      { title: 'Landing Page Optimization', desc: 'A/B test headline landing page. Gunakan social proof (jumlah ahli, winning rate). Add urgency timer untuk limited slots.', impact: 'MEDIUM' },
      { title: 'WhatsApp Funnel Setup', desc: 'Buat WhatsApp Business dengan auto-reply. Leads masuk terus dapat info package + testimoni. Response time < 5 minit.', impact: 'HIGH' },
    ],
    closed: [
      { title: 'Follow-up Automation', desc: 'Setup auto follow-up 3-5-7 hari selepas first contact. Gunakan WhatsApp broadcast dengan testimoni member yang profit.', impact: 'HIGH' },
      { title: 'Trial Period Offer', desc: 'Tawarkan 3 hari free trial untuk hesitant leads. Bagi signal real-time untuk prove value. Convert rate 40%+.', impact: 'HIGH' },
      { title: 'Payment Installment', desc: 'Offer bayar ansuran 2-3 kali untuk package mahal. Guna platform seperti Atome atau SPayLater. Lower barrier to entry.', impact: 'MEDIUM' },
    ],
    commission: [
      { title: 'Upsell VIP Package', desc: 'Pitch VIP package dengan personal coaching kepada member aktif. Margin lebih tinggi, less effort per client.', impact: 'HIGH' },
      { title: 'Referral Program Launch', desc: 'Buat program refer-a-friend dengan komisen RM100 per referral. Member jadi sales force anda secara percuma.', impact: 'HIGH' },
      { title: 'Cross-sell Broker Bonus', desc: 'Negotiate special deposit bonus dengan broker. Client deposit lebih, komisyen anda naik. Win-win situation.', impact: 'MEDIUM' },
    ],
    default: [
      { title: 'Immediate Action Required', desc: 'Metric ini memerlukan perhatian segera. Review strategi semasa dan kenal pasti bottleneck utama.', impact: 'HIGH' },
      { title: 'Resource Reallocation', desc: 'Consider reallocate budget dan manpower ke area ini. Focus effort untuk quick wins.', impact: 'HIGH' },
      { title: 'Root Cause Analysis', desc: 'Buat analisis mendalam kenapa metric ini underperform. Data-driven decision making.', impact: 'MEDIUM' },
    ],
  },
  average: { // 50-80%
    subscribers: [
      { title: 'Content Calendar Intensify', desc: 'Tingkatkan posting ke 3x sehari. Mix educational content + signal + market update. Consistency builds trust.', impact: 'MEDIUM' },
      { title: 'Cross-promotion', desc: 'Exchange shoutout dengan 2-3 channel forex lain yang bukan direct competitor. Expand reach tanpa kos.', impact: 'MEDIUM' },
      { title: 'Telegram SEO', desc: 'Optimize channel name dan description dengan keywords: "signal forex", "gold signal free". Organic discovery.', impact: 'LOW' },
    ],
    leads: [
      { title: 'Lead Scoring System', desc: 'Categorize leads Hot/Warm/Cold based on engagement. Focus 80% effort pada Hot leads untuk quick wins.', impact: 'HIGH' },
      { title: 'Retargeting Campaign', desc: 'Setup Facebook/TikTok pixel. Retarget visitors yang tak convert dengan different angle ads. Cheaper CPA.', impact: 'MEDIUM' },
      { title: 'Lead Nurturing Sequence', desc: 'Setup email/WhatsApp drip campaign 7 hari. Value-first content, soft pitch di day 5-7. Build relationship.', impact: 'MEDIUM' },
    ],
    closed: [
      { title: 'Objection Handling Scripts', desc: 'Document 10 common objections dan best responses. Train team untuk handle dengan confidence. Close rate naik 15-20%.', impact: 'HIGH' },
      { title: 'Social Proof Maximization', desc: 'Collect video testimonial dari 5 happy clients. Share di semua touchpoints. Trust factor tinggi.', impact: 'MEDIUM' },
      { title: 'Limited Time Offer', desc: 'Create FOMO dengan "3 slot sahaja untuk bulan ini". Scarcity push decision-making. Use countdown timer.', impact: 'MEDIUM' },
    ],
    commission: [
      { title: 'Volume Optimization', desc: 'Push existing clients untuk tambah deposit dengan deposit bonus. Bigger lot = higher commission.', impact: 'HIGH' },
      { title: 'Retention Campaign', desc: 'Reach out to inactive clients dengan special comeback offer. Cheaper to retain than acquire new.', impact: 'MEDIUM' },
      { title: 'Higher Tier Negotiation', desc: 'Negotiate better commission rate dengan broker based on volume. Even 0.1 pip difference adds up.', impact: 'MEDIUM' },
    ],
    default: [
      { title: 'Push for Excellence', desc: 'Anda hampir mencapai target. Tingkatkan effort 20% untuk capai 100%. Final push!', impact: 'HIGH' },
      { title: 'Process Optimization', desc: 'Review current workflow. Identify bottlenecks dan streamline processes untuk efficiency.', impact: 'MEDIUM' },
      { title: 'Team Motivation', desc: 'Galakkan team dengan small wins celebration. Morale tinggi = productivity tinggi.', impact: 'MEDIUM' },
    ],
  },
  good: { // > 80%
    subscribers: [
      { title: 'Community Building', desc: 'Start exclusive group untuk top 100 engaged members. Create sense of belonging. Retention > Acquisition.', impact: 'MEDIUM' },
      { title: 'Content Repurposing', desc: 'Turn best performing posts into carousel, video, shorts. Same content, multiple formats, wider reach.', impact: 'LOW' },
      { title: 'Ambassador Program', desc: 'Identify 10 most active members. Offer them ambassador status dengan perks. Organic word-of-mouth.', impact: 'MEDIUM' },
    ],
    leads: [
      { title: 'Lead Quality Focus', desc: 'Review lead sources. Cut low-quality sources, double down on high-converting ones. Quality > Quantity.', impact: 'HIGH' },
      { title: 'Premium Lead Magnet', desc: 'Create high-value resource seperti e-book "10 Strategi Pro Trader". Exchange untuk lead info + qualify.', impact: 'MEDIUM' },
      { title: 'Partnership Leads', desc: 'Partner dengan education providers atau financial planners. Cross-refer qualified leads. Win-win.', impact: 'MEDIUM' },
    ],
    closed: [
      { title: 'Maintain Momentum', desc: 'Document what\'s working sekarang. Create playbook untuk replicate success. Don\'t fix what ain\'t broken.', impact: 'HIGH' },
      { title: 'Upsell Strategy', desc: 'Pitch premium services kepada happy customers. Easier sell, higher lifetime value.', impact: 'MEDIUM' },
      { title: 'Referral Push', desc: 'Happy customers = best salespeople. Actively ask untuk referrals dengan incentive. Word-of-mouth powerful.', impact: 'HIGH' },
    ],
    commission: [
      { title: 'Scale Operations', desc: 'You\'re on track. Consider hiring atau expanding to new markets. Growth opportunity ahead.', impact: 'HIGH' },
      { title: 'Diversify Broker Partners', desc: 'Add 1-2 more broker partnerships. Spread risk dan tap into different trader demographics.', impact: 'MEDIUM' },
      { title: 'Premium Services Launch', desc: 'Launch high-ticket service seperti managed account atau personalized coaching. Higher margin.', impact: 'HIGH' },
    ],
    default: [
      { title: 'Excellent Performance!', desc: 'Maintain momentum ini. Document best practices untuk replicate across other metrics.', impact: 'HIGH' },
      { title: 'Scale Up', desc: 'Consider expanding scope atau target. Anda ada capacity untuk grow lebih besar.', impact: 'MEDIUM' },
      { title: 'Knowledge Transfer', desc: 'Share learnings dengan team members. Elevate overall team performance.', impact: 'MEDIUM' },
    ],
  },
};

// Get progress color based on percentage
const getProgressColor = (percent) => {
  if (isNaN(percent) || percent === null || percent === undefined) return '#6B7280'; // Gray - No data
  if (percent >= 80) return '#10B981'; // Green - Good
  if (percent >= 50) return '#F59E0B'; // Yellow - Average  
  return '#E63946'; // Red - Poor
};

// Calculate tracker metrics
const calculateTrackerMetrics = (metric, currentDay, daysInMonth, daysRemaining) => {
  // Guard against division by zero
  const target = metric.target || 0;
  const current = metric.current || 0;
  const dailyTarget = metric.dailyTarget || 0;
  
  // Calculate percent complete - handle zero target
  const percentComplete = target > 0 ? ((current / target) * 100).toFixed(1) : (current > 0 ? '—' : '0');
  const percentCompleteNum = target > 0 ? (current / target) * 100 : 0;
  
  const shortfall = target - current;
  const isOnTrack = percentCompleteNum >= ((currentDay / daysInMonth) * 100);
  
  const expectedByNow = target > 0 ? (target / daysInMonth) * currentDay : 0;
  // Guard against zero dailyTarget
  const daysBehind = dailyTarget > 0 ? Math.max(0, Math.ceil((expectedByNow - current) / dailyTarget)) : 0;
  
  const remainingTarget = target - current;
  const nextDayTarget = daysRemaining > 0 ? Math.ceil(remainingTarget / daysRemaining) : remainingTarget;
  
  return {
    percentComplete: parseFloat(percentComplete) || 0,
    shortfall: shortfall > 0 ? shortfall : 0,
    surplus: shortfall < 0 ? Math.abs(shortfall) : 0,
    isOnTrack,
    daysBehind,
    nextDayTarget: isNaN(nextDayTarget) ? 0 : nextDayTarget,
    expectedByNow: Math.round(expectedByNow) || 0
  };
};

// Generate daily data for insights - now uses real data if provided
const generateDailyData = (metric, currentDay, daysInMonth, realDailyData, dateFrom, dateTo) => {
  if (!metric) return [];
  
  // Get daily target from metric (from Channel Settings)
  const dailyTargetFromSettings = metric.dailyTarget || 0;
  const monthlyTarget = metric.target || 0;
  const calculatedDailyTarget = dailyTargetFromSettings > 0 ? dailyTargetFromSettings : (monthlyTarget / daysInMonth);
  
  // If real daily data is provided, use it
  if (realDailyData && realDailyData.length > 0) {
    const days = [];
    let cumulative = 0;
    let cumulativeTarget = 0;
    
    // Get all dates in range
    let currentDatePointer = new Date(dateFrom);
    const endDate = new Date(dateTo);
    let dayIndex = 0;
    
    while (currentDatePointer <= endDate) {
      // Format date as YYYY-MM-DD using local timezone (not UTC)
      const year = currentDatePointer.getFullYear();
      const month = String(currentDatePointer.getMonth() + 1).padStart(2, '0');
      const day = String(currentDatePointer.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;
      
      const dayNum = currentDatePointer.getDate();
      const monthNum = currentDatePointer.getMonth() + 1;
      
      // Find data for this date
      const dayData = realDailyData.find(d => d.date === dateStr);
      const actualDaily = dayData ? (dayData[metric.key] || 0) : 0;
      cumulative += actualDaily;
      
      // Calculate daily target and cumulative target
      const thisDayTarget = Math.round(calculatedDailyTarget);
      cumulativeTarget += thisDayTarget;
      
      // Calculate variance
      const variance = actualDaily - thisDayTarget;
      const variancePercent = thisDayTarget > 0 ? ((variance / thisDayTarget) * 100).toFixed(1) : '0';
      
      days.push({
        day: dayNum,
        date: `${dayNum}/${monthNum}`,
        actual: cumulative,
        target: cumulativeTarget,
        dailyActual: actualDaily,
        dailyTarget: thisDayTarget,
        variance: variance,
        variancePercent: variancePercent
      });
      
      currentDatePointer.setDate(currentDatePointer.getDate() + 1);
      dayIndex++;
    }
    
    return days;
  }
  
  // Mock data for non-real-data scenarios
  const days = [];
  const dailyTarget = calculatedDailyTarget;
  let cumulative = 0;
  
  const seededRandom = (seed) => {
    const x = Math.sin(seed) * 10000;
    return x - Math.floor(x);
  };
  
  const currentDate = new Date();
  
  for (let day = 1; day <= daysInMonth; day++) {
    const variance = (seededRandom(day * 100 + (metric.key?.length || 5)) - 0.5) * 0.4;
    const actualDaily = day <= currentDay ? Math.round(dailyTarget * (1 + variance)) : null;
    
    if (actualDaily !== null) {
      cumulative += actualDaily;
    }
    
    const targetCumulative = Math.round(dailyTarget * day);
    const varianceVal = actualDaily !== null ? cumulative - targetCumulative : null;
    
    days.push({
      day,
      date: `${day}/${currentDate.getMonth() + 1}`,
      actual: actualDaily !== null ? cumulative : null,
      target: targetCumulative,
      dailyActual: actualDaily,
      dailyTarget: Math.round(dailyTarget),
      variance: varianceVal,
      variancePercent: varianceVal !== null ? ((varianceVal / targetCumulative) * 100).toFixed(1) : null
    });
  }
  return days;
};

export function PerformanceTracker({ 
  metrics, 
  title = "Performance Tracker",
  subtitle,
  department = "marketing",
  showScrollHint = true,
  cardWidth = 280,
  realDailyData = null,  // Actual daily data from localStorage
  dateFrom = null,       // Start of date range
  dateTo = null          // End of date range
}) {
  const [insightModalOpen, setInsightModalOpen] = useState(false);
  const [selectedMetric, setSelectedMetric] = useState(null);
  const [logDepartment, setLogDepartment] = useState(department);
  
  // Calculate current date info
  const currentDate = new Date();
  const currentDay = currentDate.getDate();
  const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
  const daysRemaining = daysInMonth - currentDay;
  
  const defaultSubtitle = `Monthly Progress | Day ${currentDay} of ${daysInMonth} | ${daysRemaining} days remaining`;
  
  const openInsightModal = (metric) => {
    setSelectedMetric(metric);
    setInsightModalOpen(true);
  };

  return (
    <>
      <div className="relative w-full overflow-hidden">
        <div className="relative bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
          <SectionHeader 
            icon={Target} 
            title={title} 
            subtitle={subtitle || defaultSubtitle} 
          />
          
          <div className="p-6">
            {/* Horizontal scrolling container */}
            <div className="relative w-full overflow-hidden">
              <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-[hsl(var(--border))] scrollbar-track-transparent pb-4" style={{ maxWidth: '100%' }}>
                <div className="flex gap-5" style={{ width: 'max-content' }}>
                  {metrics.map((metric) => {
                    const Icon = metric.icon;
                    const trackerMetrics = calculateTrackerMetrics(metric, currentDay, daysInMonth, daysRemaining);
                    const progressColor = getProgressColor(trackerMetrics.percentComplete);
                    const hasSurplus = trackerMetrics.surplus > 0;
                    
                    return (
                      <div 
                        key={metric.key} 
                        className="flex-shrink-0 rounded-xl p-4 border transition-all bg-[hsl(var(--surface-elevated))]"
                        style={{ 
                          width: `${cardWidth}px`,
                          borderColor: 'hsl(var(--border))'
                        }}
                      >
                        {/* Header - Label + Insight Button */}
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-7 h-7 rounded-lg flex items-center justify-center"
                              style={{ backgroundColor: `${metric.color}20` }}
                            >
                              <Icon size={14} style={{ color: metric.color }} />
                            </div>
                            <span className="text-xs font-medium text-[#9CA3AF]">{metric.label}</span>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-[#A855F7]/40 text-[#A855F7] hover:bg-[#A855F7]/20 hover:text-[#C084FC] hover:border-[#A855F7] text-[10px] px-2 py-1 h-6"
                            onClick={() => openInsightModal(metric)}
                          >
                            <Eye size={10} className="mr-1" />
                            Insight
                          </Button>
                        </div>
                        
                        {/* Current Score - Large */}
                        <p className="text-2xl font-mono font-bold text-white mb-1">
                          {metric.prefix || ''}{metric.current.toLocaleString()}{metric.suffix || ''}
                        </p>
                        
                        {/* Target - Below score */}
                        <p className="text-xs text-[#6B7280] mb-3">
                          Target: <span className="font-mono font-semibold text-[#9CA3AF]">{metric.prefix || ''}{metric.target.toLocaleString()}{metric.suffix || ''}</span>
                        </p>
                        
                        {/* Progress Bar with 3-tier colors */}
                        <div className="mb-3">
                          <div className="flex justify-between text-[10px] mb-1">
                            <span style={{ color: progressColor }} className="font-semibold">{trackerMetrics.percentComplete}%</span>
                            <span className="text-[#6B7280]">{trackerMetrics.shortfall > 0 ? `${trackerMetrics.shortfall.toLocaleString()} left` : 'Complete'}</span>
                          </div>
                          <div className="h-2 bg-[hsl(var(--surface))] rounded-full overflow-hidden">
                            <div 
                              className="h-full rounded-full transition-all duration-500"
                              style={{ width: `${Math.min(trackerMetrics.percentComplete, 100)}%`, backgroundColor: progressColor }}
                            />
                          </div>
                        </div>
                        
                        {/* Stats Row: Shortfall/Surplus + Next Day */}
                        <div className="grid grid-cols-2 gap-2 mb-2">
                          <div className="bg-[hsl(var(--surface))] rounded p-2">
                            <p className="text-[9px] uppercase tracking-wider text-[#6B7280] mb-0.5">
                              {hasSurplus ? 'Surplus' : 'Shortfall'}
                            </p>
                            <p className="text-sm font-mono font-bold truncate" style={{ color: hasSurplus ? '#10B981' : '#E63946' }}>
                              {hasSurplus 
                                ? `+${metric.prefix || ''}${trackerMetrics.surplus.toLocaleString()}`
                                : trackerMetrics.shortfall > 0 
                                  ? `${metric.prefix || ''}${trackerMetrics.shortfall.toLocaleString()}`
                                  : '-'
                              }
                            </p>
                          </div>
                          <div className="bg-[hsl(var(--surface))] rounded p-2">
                            <p className="text-[9px] uppercase tracking-wider text-[#6B7280] mb-0.5">Next Day</p>
                            <p className="text-sm font-mono font-bold text-[#F59E0B] truncate">
                              {metric.prefix || ''}{trackerMetrics.nextDayTarget.toLocaleString()}
                            </p>
                          </div>
                        </div>
                        
                        {/* Daily Target */}
                        <div className="text-[10px] text-[#6B7280] mb-2">
                          Daily: <span className="font-mono font-semibold text-[#9CA3AF]">{metric.prefix || ''}{(metric.dailyTarget || 0).toLocaleString()}{metric.suffix || ''}</span>
                        </div>
                        
                        {/* Alert - Days/Weeks Behind or Ahead */}
                        <div 
                          className="flex items-center gap-2 p-2 rounded-lg"
                          style={{ 
                            backgroundColor: hasSurplus ? 'rgba(16, 185, 129, 0.15)' : `${progressColor}15`
                          }}
                        >
                          {(() => {
                            const dailyTargetVal = metric.dailyTarget || 1; // Avoid division by zero
                            const daysAhead = hasSurplus ? Math.ceil(trackerMetrics.surplus / dailyTargetVal) : 0;
                            const daysBehind = trackerMetrics.daysBehind;
                            
                            if (hasSurplus) {
                              const weeksAhead = Math.floor(daysAhead / 7);
                              const remainingDays = daysAhead % 7;
                              return (
                                <>
                                  <TrendingUp size={12} className="text-[#10B981]" />
                                  <span className="text-[10px] text-[#10B981]">
                                    {weeksAhead > 0 
                                      ? <><span className="font-bold">{weeksAhead}w {remainingDays}d</span> ahead of target</>
                                      : <><span className="font-bold">{daysAhead} day{daysAhead > 1 ? 's' : ''}</span> ahead of target</>
                                    }
                                  </span>
                                </>
                              );
                            } else if (daysBehind === 0 && trackerMetrics.percentComplete >= 80) {
                              return (
                                <>
                                  <TrendingUp size={12} style={{ color: progressColor }} />
                                  <span className="text-[10px]" style={{ color: progressColor }}>
                                    On track for target
                                  </span>
                                </>
                              );
                            } else if (daysBehind > 7) {
                              const weeksBehind = Math.floor(daysBehind / 7);
                              const remainingDays = daysBehind % 7;
                              return (
                                <>
                                  <AlertTriangle size={12} style={{ color: '#E63946' }} />
                                  <span className="text-[10px] text-[#E63946]">
                                    <span className="font-bold">{weeksBehind}w {remainingDays}d</span> behind schedule
                                  </span>
                                </>
                              );
                            } else if (daysBehind > 3) {
                              return (
                                <>
                                  <AlertTriangle size={12} style={{ color: '#F59E0B' }} />
                                  <span className="text-[10px] text-[#F59E0B]">
                                    <span className="font-bold">{daysBehind} days</span> behind schedule
                                  </span>
                                </>
                              );
                            } else if (daysBehind > 0) {
                              return (
                                <>
                                  <AlertTriangle size={12} style={{ color: progressColor }} />
                                  <span className="text-[10px]" style={{ color: progressColor }}>
                                    <span className="font-bold">{daysBehind} day{daysBehind > 1 ? 's' : ''}</span> behind schedule
                                  </span>
                                </>
                              );
                            } else {
                              return (
                                <>
                                  <AlertTriangle size={12} style={{ color: progressColor }} />
                                  <span className="text-[10px]" style={{ color: progressColor }}>
                                    Needs improvement
                                  </span>
                                </>
                              );
                            }
                          })()}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            
            {/* Scroll hint */}
            {showScrollHint && (
              <div className="flex items-center justify-center gap-2 mt-3 text-xs text-[#6B7280]">
                <ChevronLeft size={14} />
                <span>Scroll to view all {metrics.length} metrics</span>
                <ChevronRight size={14} />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* INSIGHT MODAL */}
      <Dialog open={insightModalOpen} onOpenChange={setInsightModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-[hsl(var(--surface))] border-[hsl(var(--border))]">
          {selectedMetric && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3 text-white">
                  <div 
                    className="w-10 h-10 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: `${selectedMetric.color}20` }}
                  >
                    {React.createElement(selectedMetric.icon, { size: 20, style: { color: selectedMetric.color } })}
                  </div>
                  <div>
                    <h2 className="text-xl font-bold">{selectedMetric.label} Insight</h2>
                    <p className="text-sm text-[#9CA3AF] font-normal">Monthly Performance Analysis</p>
                  </div>
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-6 mt-4">
                {/* Summary Cards */}
                <div className="grid grid-cols-4 gap-4">
                  {(() => {
                    const tm = calculateTrackerMetrics(selectedMetric, currentDay, daysInMonth, daysRemaining);
                    return (
                      <>
                        <div className="p-4 rounded-lg bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))]">
                          <p className="text-[10px] uppercase tracking-wider text-[#9CA3AF] mb-1">Current</p>
                          <p className="text-2xl font-mono font-bold text-white">
                            {selectedMetric.prefix || ''}{selectedMetric.current.toLocaleString()}{selectedMetric.suffix || ''}
                          </p>
                        </div>
                        <div className="p-4 rounded-lg bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))]">
                          <p className="text-[10px] uppercase tracking-wider text-[#9CA3AF] mb-1">Target</p>
                          <p className="text-2xl font-mono font-bold text-[#9CA3AF]">
                            {selectedMetric.prefix || ''}{selectedMetric.target.toLocaleString()}{selectedMetric.suffix || ''}
                          </p>
                        </div>
                        <div className="p-4 rounded-lg border" style={{ backgroundColor: `${tm.shortfall > 0 ? '#E63946' : '#10B981'}10`, borderColor: `${tm.shortfall > 0 ? '#E63946' : '#10B981'}30` }}>
                          <p className="text-[10px] uppercase tracking-wider text-[#9CA3AF] mb-1">
                            {tm.surplus > 0 ? 'Surplus' : 'Shortfall'}
                          </p>
                          <p className="text-2xl font-mono font-bold" style={{ color: tm.shortfall > 0 ? '#E63946' : '#10B981' }}>
                            {tm.surplus > 0 
                              ? `+${selectedMetric.prefix || ''}${tm.surplus.toLocaleString()}`
                              : tm.shortfall > 0 
                                ? `${selectedMetric.prefix || ''}${tm.shortfall.toLocaleString()}` 
                                : 'On Track'
                            }
                          </p>
                        </div>
                        <div className="p-4 rounded-lg border" style={{ backgroundColor: `${tm.isOnTrack ? '#10B981' : '#F59E0B'}10`, borderColor: `${tm.isOnTrack ? '#10B981' : '#F59E0B'}30` }}>
                          <p className="text-[10px] uppercase tracking-wider text-[#9CA3AF] mb-1">% Complete</p>
                          <p className="text-2xl font-mono font-bold" style={{ color: tm.isOnTrack ? '#10B981' : '#F59E0B' }}>
                            {tm.percentComplete}%
                          </p>
                        </div>
                      </>
                    );
                  })()}
                </div>
                
                {/* Trend Graph */}
                <div className="bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] rounded-xl p-4">
                  <h3 className="text-sm font-semibold text-white mb-4">Trend Graph {dateFrom && dateTo ? `(${dateFrom.toLocaleDateString('en-GB', {day: '2-digit', month: 'short'})} - ${dateTo.toLocaleDateString('en-GB', {day: '2-digit', month: 'short'})})` : '(1st - Current Date)'}</h3>
                  <ResponsiveContainer width="100%" height={250}>
                    <AreaChart data={generateDailyData(selectedMetric, currentDay, daysInMonth, realDailyData, dateFrom, dateTo).filter(d => d.actual !== null)}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="date" stroke="#9CA3AF" style={{ fontSize: '10px' }} />
                      <YAxis stroke="#9CA3AF" style={{ fontSize: '10px' }} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--surface))', 
                          border: '1px solid hsl(var(--border))', 
                          borderRadius: '8px',
                          color: '#fff',
                          fontSize: '12px'
                        }}
                        formatter={(value, name) => [
                          `${selectedMetric.prefix || ''}${value?.toLocaleString() || '-'}${selectedMetric.suffix || ''}`,
                          name
                        ]}
                      />
                      <Legend />
                      <Area 
                        type="monotone" 
                        dataKey="actual" 
                        stroke={selectedMetric.color} 
                        fill={selectedMetric.color} 
                        fillOpacity={0.2} 
                        strokeWidth={2}
                        name="Actual (Cumulative)"
                      />
                      <Area 
                        type="monotone" 
                        dataKey="target" 
                        stroke="#9CA3AF" 
                        fill="#9CA3AF" 
                        fillOpacity={0.1} 
                        strokeWidth={2}
                        strokeDasharray="5 5"
                        name="Target (Cumulative)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
                
                {/* Daily Values Table */}
                <div className="bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] rounded-xl p-4">
                  <h3 className="text-sm font-semibold text-white mb-4">Daily Values (Whole Month)</h3>
                  <div className="overflow-x-auto max-h-[200px] overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="sticky top-0 bg-[hsl(var(--surface-elevated))]">
                        <tr className="border-b border-[hsl(var(--border))]">
                          <th className="text-left p-2 text-[#9CA3AF]">Day</th>
                          <th className="text-right p-2 text-[#9CA3AF]">Actual</th>
                          <th className="text-right p-2 text-[#9CA3AF]">Target</th>
                          <th className="text-right p-2 text-[#9CA3AF]">Variance</th>
                          <th className="text-right p-2 text-[#9CA3AF]">%</th>
                        </tr>
                      </thead>
                      <tbody>
                        {generateDailyData(selectedMetric, currentDay, daysInMonth, realDailyData, dateFrom, dateTo).map((day) => (
                          <tr key={day.day} className="border-b border-[hsl(var(--border))/50]">
                            <td className="p-2 text-white font-mono">{day.date}</td>
                            <td className="p-2 text-right text-white font-mono">
                              {day.dailyActual !== null ? `${selectedMetric.prefix || ''}${day.dailyActual.toLocaleString()}` : '-'}
                            </td>
                            <td className="p-2 text-right text-[#9CA3AF] font-mono">
                              {selectedMetric.prefix || ''}{(day.dailyTarget || 0).toLocaleString()}
                            </td>
                            <td className="p-2 text-right font-mono" style={{ color: day.variance !== null ? (day.variance >= 0 ? '#10B981' : '#E63946') : '#6B7280' }}>
                              {day.variance !== null ? `${day.variance >= 0 ? '+' : ''}${selectedMetric.prefix || ''}${day.variance.toLocaleString()}` : '-'}
                            </td>
                            <td className="p-2 text-right font-mono" style={{ color: day.variancePercent !== null ? (parseFloat(day.variancePercent) >= 0 ? '#10B981' : '#E63946') : '#6B7280' }}>
                              {day.variancePercent !== null ? `${parseFloat(day.variancePercent) >= 0 ? '+' : ''}${day.variancePercent}%` : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                
                {/* AI Strategy Suggestions */}
                <div className="bg-gradient-to-br from-[#F59E0B]/10 to-[#E63946]/10 border border-[#F59E0B]/30 rounded-xl p-4">
                  <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                    <Lightbulb size={16} className="text-[#F59E0B]" />
                    AI Strategy Suggestions
                    <Badge className="ml-2 bg-[hsl(var(--surface))]/50 text-[#9CA3AF] text-[10px]">
                      {selectedMetric?.label || 'Metric'}
                    </Badge>
                  </h3>
                  
                  {(() => {
                    const percent = selectedMetric ? (selectedMetric.current / selectedMetric.target * 100) : 0;
                    const metricKey = selectedMetric?.key || 'default';
                    
                    const tier = percent < 50 ? 'poor' : percent < 80 ? 'average' : 'good';
                    const relevantStrategies = strategyDatabase[tier][metricKey] || strategyDatabase[tier]['default'];
                    
                    return (
                      <div className="space-y-3">
                        {relevantStrategies.map((strategy, idx) => (
                          <div key={idx} className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-lg p-3">
                            <div className="flex items-start justify-between mb-2">
                              <h4 className="text-sm font-semibold text-white">{idx + 1}. {strategy.title}</h4>
                              <Badge className={`text-[9px] ${strategy.impact === 'HIGH' ? 'bg-[#10B981]/20 text-[#10B981]' : strategy.impact === 'MEDIUM' ? 'bg-[#F59E0B]/20 text-[#F59E0B]' : 'bg-[#6B7280]/20 text-[#6B7280]'}`}>
                                {strategy.impact} IMPACT
                              </Badge>
                            </div>
                            <p className="text-xs text-[#9CA3AF] leading-relaxed">{strategy.desc}</p>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>

                {/* Submit to Log Button */}
                <div className="bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] rounded-xl p-4">
                  <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                    <FileEdit size={16} className="text-[#3B82F6]" />
                    Quick Action - Submit to Log
                  </h3>
                  <p className="text-xs text-[#9CA3AF] mb-3">
                    Submit strategi terpilih terus ke Log untuk tracking oleh team.
                  </p>
                  <div className="flex items-center gap-3">
                    <Select value={logDepartment} onValueChange={setLogDepartment}>
                      <SelectTrigger className="w-40 bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white">
                        <SelectValue placeholder="Department" />
                      </SelectTrigger>
                      <SelectContent className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]">
                        <SelectItem value="marketing" className="text-white">Marketing</SelectItem>
                        <SelectItem value="sales" className="text-white">Sales</SelectItem>
                        <SelectItem value="operation" className="text-white">Operation</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button 
                      className="flex-1 bg-gradient-to-r from-[#3B82F6] to-[#06B6D4] hover:from-[#2563EB] hover:to-[#0891B2] text-white"
                      onClick={() => {
                        // Store data for Log page
                        const logData = {
                          metric: selectedMetric.label,
                          department: logDepartment,
                          currentValue: selectedMetric.current,
                          targetValue: selectedMetric.target,
                          timestamp: new Date().toISOString()
                        };
                        localStorage.setItem('pendingLogEntry', JSON.stringify(logData));
                        alert(`Log entry prepared untuk ${selectedMetric.label}!\n\nDepartment: ${logDepartment}\nMetric: ${selectedMetric.label}\nCurrent: ${selectedMetric.prefix || ''}${selectedMetric.current.toLocaleString()}\n\nNavigate to Log page untuk complete entry.`);
                      }}
                    >
                      <FileEdit size={14} className="mr-2" />
                      Submit to Log
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default PerformanceTracker;
